import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Container from "@/components/Container";
import { api } from "@/lib/api";
import {
  Coins, TrendingUp, Hammer, BadgeCheck, ExternalLink, Search, FileSpreadsheet,
} from "lucide-react";

const STAGES = [
  { key: "all",     label: "All" },
  { key: "offered", label: "Offers Sent" },
  { key: "held",    label: "Acquired / Held" },
  { key: "sold",    label: "Sold" },
];

const fmt = (n) => "£" + (Number(n) || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 });
const pct = (n) => `${(Number(n) || 0).toFixed(1)}%`;

export default function Portfolio() {
  const [data, setData] = useState({ rows: [], summary: {} });
  const [stage, setStage] = useState("all");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("profit");
  const [sortDir, setSortDir] = useState("desc");

  const load = () => {
    api.get("/portfolio/performance")
      .then((r) => setData(r.data || { rows: [], summary: {} }))
      .catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    let r = data.rows || [];
    if (stage !== "all") r = r.filter((x) => x.stage === stage);
    if (search) {
      const s = search.toLowerCase();
      r = r.filter((x) =>
        (x.title || "").toLowerCase().includes(s) ||
        (x.city || "").toLowerCase().includes(s) ||
        (x.postcode || "").toLowerCase().includes(s)
      );
    }
    r = [...r].sort((a, b) => {
      const av = a[sortKey] ?? 0;
      const bv = b[sortKey] ?? 0;
      const cmp = typeof av === "string" ? av.localeCompare(bv) : av - bv;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return r;
  }, [data, stage, search, sortKey, sortDir]);

  const setSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("desc"); }
  };

  const exportCsv = () => {
    const headers = [
      "Title","City","Postcode","Stage","Offer £","Offer status","Purchased £","Reno £",
      "Holding £","Other £","Total invested £","Sold £","Profit £","ROI %",
    ];
    const lines = [headers.join(",")].concat(
      rows.map((r) => [
        `"${(r.title || "").replace(/"/g, '""')}"`,
        r.city || "", r.postcode || "", r.stage,
        r.offer_submitted_price || "", r.offer_status || "",
        r.actual_purchase_price || "", r.actual_renovation_spend || "",
        r.actual_holding_costs || "", r.actual_other_costs || "",
        r.total_invested || 0, r.actual_sold_price || "",
        r.profit || 0, r.roi_percentage || 0,
      ].join(","))
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `rubys-portfolio-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const s = data.summary || {};

  return (
    <Container className="py-8 md:py-12">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline">Deals</p>
          <h1 className="mt-2 text-4xl md:text-5xl font-serif text-ivory" data-testid="portfolio-heading">
            Portfolio Performance
          </h1>
          <p className="text-ivory/50 mt-2 text-sm">
            Every property you&apos;ve offered on, acquired or sold — with money in, money out and ROI.
          </p>
        </div>
        <button onClick={exportCsv} data-testid="portfolio-export-csv" className="btn-outline">
          <FileSpreadsheet size={14} /> Export CSV
        </button>
      </div>

      {/* Summary KPIs */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi icon={Coins}     label="Total invested"  value={fmt(s.total_invested)}  accent testId="kpi-invested" />
        <Kpi icon={TrendingUp} label="Total returned"  value={fmt(s.total_returned)}  testId="kpi-returned" />
        <Kpi icon={BadgeCheck} label="Realised profit" value={fmt(s.total_profit)}    accent testId="kpi-profit" />
        <Kpi icon={Hammer}     label="Average ROI"     value={pct(s.avg_roi_percentage)} testId="kpi-roi" />
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
        <MiniStat label="Tracking" value={s.count || 0} k="count" />
        <MiniStat label="Offers sent" value={s.offered_count || 0} k="offered" />
        <MiniStat label="Held" value={s.held_count || 0} k="held" />
        <MiniStat label="Sold" value={s.sold_count || 0} k="sold" />
      </div>

      {/* Filters */}
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="flex gap-2 flex-wrap">
          {STAGES.map((st) => (
            <button
              key={st.key}
              onClick={() => setStage(st.key)}
              data-testid={`stage-tab-${st.key}`}
              className={`px-3 py-1.5 text-[0.7rem] uppercase tracking-[0.18em] border ${
                stage === st.key ? "border-ruby text-ruby" : "border-charcoal-600 text-ivory/60 hover:text-ivory"
              }`}
            >
              {st.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[220px] max-w-md ml-auto">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ivory/40" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title, city, postcode…"
            data-testid="portfolio-search"
            className="w-full bg-charcoal-800 border border-charcoal-600 pl-9 pr-3 py-2 text-sm"
          />
        </div>
      </div>

      {/* Table */}
      <div className="mt-6 luxury-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="portfolio-table">
          <thead className="bg-charcoal-700 text-ivory/60 text-[0.65rem] uppercase tracking-[0.18em]">
            <tr>
              <Th label="Property" onClick={() => setSort("title")} active={sortKey === "title"} dir={sortDir} />
              <Th label="Stage" />
              <Th label="Offer" align="right" onClick={() => setSort("offer_submitted_price")} active={sortKey === "offer_submitted_price"} dir={sortDir} />
              <Th label="Bought" align="right" onClick={() => setSort("actual_purchase_price")} active={sortKey === "actual_purchase_price"} dir={sortDir} />
              <Th label="Reno" align="right" onClick={() => setSort("actual_renovation_spend")} active={sortKey === "actual_renovation_spend"} dir={sortDir} />
              <Th label="Invested" align="right" onClick={() => setSort("total_invested")} active={sortKey === "total_invested"} dir={sortDir} />
              <Th label="Sold" align="right" onClick={() => setSort("actual_sold_price")} active={sortKey === "actual_sold_price"} dir={sortDir} />
              <Th label="Profit" align="right" onClick={() => setSort("profit")} active={sortKey === "profit"} dir={sortDir} />
              <Th label="ROI" align="right" onClick={() => setSort("roi_percentage")} active={sortKey === "roi_percentage"} dir={sortDir} />
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={10} className="p-10 text-center text-ivory/40">
                Nothing tracked yet. Open a property and fill in <span className="text-ruby">Offer &amp; Deal Tracker</span> — it&apos;ll appear here.
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-charcoal-600/60 hover:bg-charcoal-800/40" data-testid={`portfolio-row-${r.id}`}>
                <td className="p-3">
                  <div className="text-ivory">{r.title}</div>
                  <div className="text-[0.65rem] text-ivory/40">{r.city} {r.postcode}</div>
                </td>
                <td className="p-3"><StageBadge stage={r.stage} /></td>
                <td className="p-3 text-right text-ivory/80">{r.offer_submitted_price ? fmt(r.offer_submitted_price) : "—"}</td>
                <td className="p-3 text-right text-ivory/80">{r.actual_purchase_price ? fmt(r.actual_purchase_price) : "—"}</td>
                <td className="p-3 text-right text-ivory/80">{r.actual_renovation_spend ? fmt(r.actual_renovation_spend) : "—"}</td>
                <td className="p-3 text-right text-ivory">{r.total_invested ? fmt(r.total_invested) : "—"}</td>
                <td className="p-3 text-right text-ivory/80">{r.actual_sold_price ? fmt(r.actual_sold_price) : "—"}</td>
                <td className={`p-3 text-right ${r.profit > 0 ? "text-gold" : r.profit < 0 ? "text-ruby-light" : "text-ivory/60"}`}>
                  {r.profit ? fmt(r.profit) : "—"}
                </td>
                <td className={`p-3 text-right ${r.roi_percentage > 0 ? "text-gold" : r.roi_percentage < 0 ? "text-ruby-light" : "text-ivory/60"}`}>
                  {r.roi_percentage ? pct(r.roi_percentage) : "—"}
                </td>
                <td className="p-3 text-right">
                  <Link to={`/admin/properties/${r.id}`} className="btn-outline !py-1 !px-3 !text-[0.65rem]">
                    Open <ExternalLink size={11} />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Container>
  );
}

function Kpi({ icon: Icon, label, value, accent, testId }) {
  return (
    <div className="luxury-card p-4 md:p-6 min-w-0" data-testid={testId}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-[0.6rem] md:text-[0.65rem] uppercase tracking-[0.12em] md:tracking-[0.2em] text-ivory/50 leading-relaxed min-w-0 break-words">{label}</p>
        <Icon size={16} className={`shrink-0 ${accent ? "text-gold" : "text-ivory/40"}`} strokeWidth={1.5} />
      </div>
      <p className="mt-3 md:mt-5 text-xl md:text-3xl font-serif text-ivory break-words">{value}</p>
    </div>
  );
}

function MiniStat({ label, value, k }) {
  return (
    <div className="border border-charcoal-600 px-4 py-3" data-testid={`mini-${k}`}>
      <p className="text-[0.6rem] uppercase tracking-[0.2em] text-ivory/40">{label}</p>
      <p className="mt-1 text-xl font-serif text-ivory">{value}</p>
    </div>
  );
}

function Th({ label, align = "left", onClick, active, dir }) {
  return (
    <th
      onClick={onClick}
      className={`p-3 text-${align} ${onClick ? "cursor-pointer hover:text-ivory" : ""} ${active ? "text-gold" : ""}`}
    >
      {label}{active ? (dir === "asc" ? " ↑" : " ↓") : ""}
    </th>
  );
}

function StageBadge({ stage }) {
  const map = {
    offered: { c: "border-bronze/60 text-bronze", l: "Offer Sent" },
    held:    { c: "border-gold/60 text-gold",     l: "Held" },
    sold:    { c: "border-ruby/60 text-ruby-light", l: "Sold" },
    tracking:{ c: "border-charcoal-600 text-ivory/60", l: "Tracking" },
  };
  const m = map[stage] || map.tracking;
  return (
    <span className={`inline-block px-2 py-1 border text-[0.6rem] uppercase tracking-[0.18em] ${m.c}`}>
      {m.l}
    </span>
  );
}
